/**
 * planItem.ts — map a checkout plan choice onto the funnel's PlanItem shape.
 *
 * WHY (Part F / R28): the portal holds `{ productKey, medicationName, months,
 * monthlyPence }`. Mapping `productKey` → `planSku` and `medicationName` →
 * `planName` would send a named POM brand into GA4 against an identifiable ads
 * profile — special-category health data to ad-tech. Orders-service already
 * refuses to do that server-side; the browser funnel must not undermine it.
 * Derive a TERM-BASED, non-clinical SKU and label instead (`plan_3m` /
 * "3-month plan") so Salman can still segment by term and value.
 *
 * `ecommerce.item_category` is hard-coded inside `dataLayer.ts` and stays there
 * — not this mapper's call. It is now `'treatment_plan'`: the DPO question that
 * used to sit here is CLOSED, and the clinical `'weight-loss'` string is gone
 * (see the tracking README, "Health signals").
 */

/** The closed plan shape `trackAddToCart` / `trackAddPaymentInfo` accept. */
export interface TrackingPlanItem {
  planSku: string;
  planName: string;
  /** Price as a NUMBER in major units (e.g. 149.00), never a string / never pence. */
  planPrice: number;
}

/** Minimum fields needed to build a term-based tracking plan. */
export interface TrackingPlanSource {
  months: number;
  monthlyPence: number;
}

/**
 * Term-based, non-clinical plan for the acquisition funnel.
 * NEVER passes `productKey` or `medicationName` through.
 */
export function toTrackingPlan(choice: TrackingPlanSource): TrackingPlanItem {
  const months = choice.months;
  return {
    planSku: `plan_${months}m`,
    planName: `${months}-month plan`,
    // WHY (term total, not monthly): the item is named "N-month plan" and Salman
    // bids to revenue — GA4 `value` must be what the order is worth for the term
    // (`monthlyPence * months`), not a single month. Integer pence → major units
    // at the tracking boundary only.
    planPrice: (choice.monthlyPence * months) / 100,
  };
}
